import { useCallback, useMemo } from 'react';
import { EventQueueWireDto } from '@mark43/cad-api';
import { useEventQueueViews } from '../event-queues/event-queue-views-provider';
import {
    getClientEventAutocompleteOptions,
    reconcileEventAutocompleteOptions,
} from './local-first-event-autocomplete';

export interface LocalFirstEventAutocomplete<TOption> {
    /**
     * Matched active events for `query`, mapped to the caller's option type, in logical
     * best-first order. Returns `[]` when the feature is disabled or the query is empty.
     */
    getClientOptions: (query: string) => TOption[];
    /** Append server-only options (by event id) after the stable client block. */
    reconcile: (clientOptions: TOption[], serverOptions: TOption[]) => TOption[];
}

/**
 * Reusable local-first event-# autocomplete wiring, shared by the command line and the form
 * event pickers. Reads the client's live event-queue-views map and exposes an instant
 * client-side filter plus an append-only reconciliation with the server response.
 *
 * `isEnabled` is passed in (typically `useEnabledFeatures().localFirstEventAutocompleteEnabled`)
 * so this hook stays decoupled from the enabled-features context and easy to test. Pass stable
 * (module-level) `mapEventQueueViewToOption` / `getEventId` functions so the returned
 * callbacks stay referentially stable.
 *
 * This hook intentionally does not manage selection/highlight state — that's store-specific
 * (the command line's bottom-anchored `CommandLineEditorStore` vs. a form picker's own
 * component state), so callers derive it themselves using `resolveEventAutocompleteSelectedId`
 * from `./local-first-event-autocomplete`.
 */
export function useLocalFirstEventAutocomplete<TOption>({
    isEnabled,
    mapEventQueueViewToOption,
    getEventId,
}: {
    isEnabled: boolean;
    mapEventQueueViewToOption: (view: EventQueueWireDto) => TOption;
    getEventId: (option: TOption) => number;
}): LocalFirstEventAutocomplete<TOption> {
    const { eventQueueViewsRef } = useEventQueueViews();

    const getClientOptions = useCallback(
        (query: string): TOption[] => {
            if (!isEnabled || !query) {
                return [];
            }
            const eventQueueViews = Object.values(eventQueueViewsRef.current);
            return getClientEventAutocompleteOptions(
                eventQueueViews,
                query,
                mapEventQueueViewToOption
            );
        },
        [isEnabled, eventQueueViewsRef, mapEventQueueViewToOption]
    );

    const reconcile = useCallback(
        (clientOptions: TOption[], serverOptions: TOption[]): TOption[] =>
            reconcileEventAutocompleteOptions(clientOptions, serverOptions, getEventId),
        [getEventId]
    );

    return useMemo(() => ({ getClientOptions, reconcile }), [getClientOptions, reconcile]);
}
